import { useState } from 'react';
import { RouterProvider } from '@tanstack/react-router';
import { ConfirmProvider } from '@kolectra/ui';
import type { SessionInfo } from '@kolectra/shared/schemas';
import { Permission, type PermissionAtom } from '@kolectra/shared/permissions';
import { effectiveWebTeams } from './auth.js';
import { LanguageProvider } from './language.js';
import { ProjectDashboardPage } from './routes/dashboard.js';
import { router } from './router.js';
import {
  ProjectSurfaceProvider,
  PROJECT_WORK_PERMS,
  type Surface,
  type ProjectSurfaceValue,
} from './project-surface.js';

/**
 * Shell for a teamless PROJECT user. Such a user can hold more than one project web
 * surface — the project-wide **overview** (`can_view_project_reports`) and the **work**
 * surfaces of the team app (Judgement etc., via synthesized teams). Those render in
 * different trees (the standalone dashboard vs the team router), so this shell holds a
 * `surface` toggle and exposes it via context; each surface renders a nav entry to switch.
 */
export function ProjectUserApp({ session }: { session: SessionInfo }) {
  const perms = session.project?.permissions ?? [];
  const hasOverview = perms.includes(Permission.CAN_VIEW_PROJECT_REPORTS);
  const hasWork = effectiveWebTeams(session).some((t) =>
    t.permissions.some((p) => PROJECT_WORK_PERMS.includes(p as PermissionAtom)),
  );
  const [surface, setSurface] = useState<Surface>(hasOverview ? 'overview' : 'work');

  const value: ProjectSurfaceValue = { surface, setSurface, hasOverview, hasWork };
  const showOverview = surface === 'overview' && hasOverview;

  return (
    <ProjectSurfaceProvider value={value}>
      {showOverview ? (
        <ProjectDashboardPage />
      ) : (
        // The team app on synthesized teams — Judgement etc., scoped to assigned languages.
        <LanguageProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
          </ConfirmProvider>
        </LanguageProvider>
      )}
    </ProjectSurfaceProvider>
  );
}
